import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Crown, X, Video, Mic, Headphones, Disc, Music, Drum,
  ShoppingBag, GraduationCap, Radio, Image, Users, Settings,
  ExternalLink, Copy, Home, Layers, Zap, Shirt, Play, Wrench
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

/**
 * AdminCommandCenter
 * Global floating admin panel mounted in Layout. Visible only to admin/super_admin.
 * Gives one-click access to every gated feature so the admin never has to
 * fill forms, click through wizards, or hit paywalls to test the site.
 */
const generateRoomCode = () => Math.random().toString(36).substring(2, 8).toUpperCase();

const AdminCommandCenter = () => {
  const { isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [dockOpen, setDockOpen] = useState(false);
  const isAdminUser = typeof isAdmin === 'function' ? isAdmin() : false;

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!isAdminUser) return null;

  // Direct action: spin up a live studio room and enter as host
  const enterLiveStudioAsHost = () => {
    const code = generateRoomCode();
    toast.success(`Room ${code} — entering as host`);
    window.location.href = `/video-room/${code}/host`;
  };

  // Direct action: quick-preview a purchase page with admin bypass flag
  const goTo = (path) => {
    setOpen(false);
    window.location.href = path;
  };

  // Sections of shortcuts
  const sections = [
    {
      title: 'Live & Studio',
      color: 'from-green-500/20 to-emerald-500/20 border-green-500/40',
      items: [
        {
          label: 'Start Live Room (Host)',
          hint: 'Spin up a new video room, become the host, and copy the guest link',
          icon: Video,
          onClick: enterLiveStudioAsHost,
          testid: 'acc-start-live-room',
        },
        {
          label: 'Live Studio Booking',
          hint: 'Open the public /live-studio page with the invite panel',
          icon: Radio,
          onClick: () => goTo('/live-studio'),
          testid: 'acc-live-studio',
        },
        {
          label: 'Recording Studio (DAW)',
          hint: 'Jump straight into the admin DAW — skip all forms',
          icon: Headphones,
          onClick: () => goTo('/admin/studio-session'),
          testid: 'acc-daw',
        },
        {
          label: 'Recording Studio Page',
          hint: 'Public /recording-studio (admin banner visible)',
          icon: Mic,
          onClick: () => goTo('/recording-studio'),
          testid: 'acc-recording-studio',
        },
        {
          label: 'Batucada DAW',
          hint: 'Brasilian Grooves sample player',
          icon: Drum,
          onClick: () => goTo('/batucada'),
          testid: 'acc-batucada',
        },
        {
          label: 'Play Along',
          hint: 'Full unlimited access to lessons + backing tracks',
          icon: Play,
          onClick: () => goTo('/play-along'),
          testid: 'acc-play-along',
        },
      ],
    },
    {
      title: 'Paid Content (Admin Free)',
      color: 'from-yellow-500/20 to-amber-500/20 border-yellow-500/40',
      items: [
        {
          label: 'Music Store',
          hint: 'Track catalog — admin free download available',
          icon: Music,
          onClick: () => goTo('/music-store'),
          testid: 'acc-music-store',
        },
        {
          label: 'Quick Buy',
          hint: 'Music purchase page — admin bypass',
          icon: Disc,
          onClick: () => goTo('/quick-buy'),
          testid: 'acc-quick-buy',
        },
        {
          label: 'Pay Per View',
          hint: 'PPV videos — admin free access',
          icon: Play,
          onClick: () => goTo('/ppv'),
          testid: 'acc-ppv',
        },
        {
          label: 'Courses',
          hint: 'Course catalog — admin free enrollment',
          icon: GraduationCap,
          onClick: () => goTo('/courses'),
          testid: 'acc-courses',
        },
        {
          label: 'Costume Shop',
          hint: 'Costume checkout — admin free',
          icon: Shirt,
          onClick: () => goTo('/costume-shop'),
          testid: 'acc-costume',
        },
      ],
    },
    {
      title: 'Admin Tools',
      color: 'from-purple-500/20 to-pink-500/20 border-purple-500/40',
      items: [
        {
          label: 'Admin Dashboard',
          hint: 'Full site administration',
          icon: Settings,
          onClick: () => goTo('/admin'),
          testid: 'acc-admin-dashboard',
        },
        {
          label: 'Session Manager',
          hint: 'Manage all live sessions',
          icon: Users,
          onClick: () => goTo('/admin/live-studio/sessions'),
          testid: 'acc-session-manager',
        },
        {
          label: 'Content / Pages',
          hint: 'Edit page content',
          icon: Layers,
          onClick: () => goTo('/admin/pages'),
          testid: 'acc-admin-pages',
        },
        {
          label: 'Flyer GIF Uploads',
          hint: 'Upload the 12 dance GIFs for the circular flyer',
          icon: Layers,
          onClick: () => goTo('/admin/flyer-gifs'),
          testid: 'acc-flyer-gifs',
        },
        {
          label: 'Flyer Generator',
          hint: 'Downloadable class flyer (PNG + PDF)',
          icon: Layers,
          onClick: () => goTo('/admin/flyer'),
          testid: 'acc-flyer',
        },
        {
          label: 'Reel Builder',
          hint: 'Stitch MP4/GIF clips into an IG/TikTok reel',
          icon: Layers,
          onClick: () => goTo('/admin/reel'),
          testid: 'acc-reel',
        },
        {
          label: 'Track Repair',
          hint: 'Scan every song — find which ones deliver only a 30s sample',
          icon: Wrench,
          onClick: () => goTo('/admin/track-repair'),
          testid: 'acc-track-repair',
        },
        {
          label: 'Payment settings',
          hint: 'Paste your Stripe key, no redeploy needed.',
          icon: Layers,
          onClick: () => goTo('/admin/payment-settings'),
          testid: 'acc-payment-settings',
        },
        {
          label: 'Sync preview → live',
          hint: 'Pull missing content from preview. Never touches users, orders or payments.',
          icon: Layers,
          onClick: () => goTo('/admin/content-sync'),
          testid: 'acc-content-sync',
        },
        {
          label: 'Gallery',
          hint: 'Media library',
          icon: Image,
          onClick: () => goTo('/gallery'),
          testid: 'acc-gallery',
        },
      ],
    },
  ];

  return (
    <>
      {/* Floating trigger button (bottom-left so it doesn't collide with chat widget) */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="admin-command-center-trigger"
        aria-label="Open Admin Command Center"
        className="fixed left-4 bottom-20 z-[60] group flex items-center gap-2 px-4 py-3 rounded-full bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-bold shadow-2xl shadow-yellow-500/40 hover:scale-105 transition-transform"
      >
        <Crown className="w-5 h-5" />
        <span className="hidden sm:inline text-sm">Admin</span>
      </button>

      {/* Panel */}
      {open && (
        <div
          className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
          onClick={() => setOpen(false)}
          data-testid="admin-command-center-panel"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-3xl bg-[#111] rounded-t-3xl sm:rounded-3xl border-2 border-yellow-500/40 shadow-2xl max-h-[92vh] overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 bg-gradient-to-r from-yellow-500/20 to-amber-500/10 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-yellow-500 text-black flex items-center justify-center">
                  <Crown className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-white font-bold text-lg leading-tight">Admin Command Center</h2>
                  <p className="text-yellow-100/70 text-xs">One-click access to everything — no forms, no paywalls</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="acc-close-btn"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="overflow-y-auto px-5 py-5 space-y-5">
              {sections.map((section) => (
                <div
                  key={section.title}
                  className={`rounded-2xl border bg-gradient-to-br ${section.color} p-4`}
                >
                  <h3 className="text-white font-bold text-sm uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Zap className="w-4 h-4 text-yellow-300" />
                    {section.title}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {section.items.map((item) => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.label}
                          type="button"
                          onClick={item.onClick}
                          data-testid={item.testid}
                          className="flex items-start gap-3 p-3 rounded-xl bg-black/40 hover:bg-black/60 border border-white/5 hover:border-yellow-500/40 text-left transition-all group"
                        >
                          <div className="w-9 h-9 rounded-lg bg-white/10 group-hover:bg-yellow-500/20 flex items-center justify-center shrink-0">
                            <Icon className="w-5 h-5 text-yellow-300" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-white font-semibold text-sm truncate">{item.label}</p>
                            <p className="text-white/50 text-xs leading-tight mt-0.5">{item.hint}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Footer hint */}
              <div className="text-center text-xs text-white/40 pt-1 pb-2">
                Press <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-white/70 font-mono text-[10px]">Esc</kbd> to close · Only visible to admin users
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminCommandCenter;
